import { useEffect, useState } from 'react';
import { apiFetch } from '../config/api';
import PhotoUploadField from '../components/PhotoUploadField';

const RELATIONSHIPS = ['Parent', 'Guardian', 'Sibling', 'Spouse', 'Relative', 'Friend', 'Neighbor', 'Other'];
const CONTACT_TYPE_LABELS = {
  parent: 'Parent',
  guardian: 'Guardian',
  driver: 'Driver',
};

function contactTypeLabel(type) {
  return CONTACT_TYPE_LABELS[type] || 'Contact';
}

function buildParentForm(parent) {
  if (!parent) {
    return {
      f_name: '', l_name: '', email: '', phone: '',
      street_address: '', city: '', state: '', zip_code: '',
      driver_license_number: '', vehicle_make_model: '', car_color: '', license_plate: '',
      emergency_f_name: '', emergency_l_name: '', emergency_backup_phone: '',
      emergency_relationship: '', notifications_enabled: '1',
      photo_relative_path: null,
    };
  }

  return {
    f_name: parent.f_name || '',
    l_name: parent.l_name || '',
    email: parent.email || '',
    phone: parent.phone || '',
    street_address: parent.street_address || '',
    city: parent.city || '',
    state: parent.state || '',
    zip_code: parent.zip_code || '',
    driver_license_number: parent.driver_license_number || '',
    vehicle_make_model: parent.vehicle_make_model || '',
    car_color: parent.car_color || '',
    license_plate: parent.license_plate || '',
    emergency_f_name: parent.emergency_f_name || '',
    emergency_l_name: parent.emergency_l_name || '',
    emergency_backup_phone: parent.emergency_backup_phone || '',
    emergency_relationship: parent.emergency_relationship || '',
    notifications_enabled: parent.notifications_enabled ? '1' : '0',
    photo_relative_path: parent.photo_relative_path || null,
  };
}

function parseSchoolIds(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function ParentForm({ token, navigate, schoolId, parent, parentId, returnPage = 'parents' }) {
  const editParentId = parent?.id || parentId || null;
  const isEdit = !!editParentId;
  const [parentRecord, setParentRecord] = useState(parent || null);
  const contactType = isEdit ? (parentRecord?.type || parent?.type || 'parent') : 'parent';
  const contactLabel = contactTypeLabel(contactType);
  const directoryLabel = returnPage === 'drivers' ? 'Drivers' : 'Parents';
  const parentSchoolIds = parseSchoolIds(parent?.school_ids);
  const parentRecordSchoolIds = parseSchoolIds(parentRecord?.school_ids);
  const effectiveSchoolId = schoolId || parentSchoolIds[0] || parentRecordSchoolIds[0] || null;
  const recordSchoolMismatch = Boolean(
    parentRecord
    && effectiveSchoolId
    && !parentRecordSchoolIds.map(Number).includes(Number(effectiveSchoolId))
  );

  const [form, setForm] = useState(() => buildParentForm(parent));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit && !parent);
  // Existing account returned by a 409 on create — we offer linking it to this
  // school instead of creating a duplicate.
  const [existingParent, setExistingParent] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!parent) return;
    setParentRecord(parent);
    setForm(buildParentForm(parent));
    setLoading(false);
  }, [parent]);

  useEffect(() => {
    if (!isEdit || parent || !editParentId) return undefined;

    let cancelled = false;
    setLoading(true);

    apiFetch(`/contacts/${editParentId}`, {}, token)
      .then((res) => {
        if (cancelled) return;
        setParentRecord(res.data);
        setForm(buildParentForm(res.data));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editParentId, isEdit, parent, token]);

  const set = (field) => (e) => setForm((currentForm) => ({ ...currentForm, [field]: e.target.value }));

  const handleLinkExisting = async () => {
    if (!existingParent?.id || !effectiveSchoolId) return;
    setLinking(true);
    setError('');
    try {
      await apiFetch(`/contacts/${existingParent.id}/schools`, {
        method: 'POST',
        body: JSON.stringify({ school_id: effectiveSchoolId }),
      }, token);
      navigate(returnPage, returnPage === 'drivers'
        ? { schoolId: effectiveSchoolId, selectDriverId: existingParent.id }
        : effectiveSchoolId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLinking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setExistingParent(null);
    if (recordSchoolMismatch) {
      setError(`This ${contactLabel.toLowerCase()} does not belong to the selected school.`);
      return;
    }
    setSaving(true);
    try {
      const body = {
        type: contactType,
        school_ids: [effectiveSchoolId],
        f_name: form.f_name,
        l_name: form.l_name,
        email: form.email,
        phone: form.phone,
        street_address: form.street_address,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        driver_license_number: form.driver_license_number,
        vehicle_make_model: form.vehicle_make_model || null,
        car_color: form.car_color || null,
        license_plate: form.license_plate,
        photo_relative_path: form.photo_relative_path || 'placeholder.jpg',
        emergency_f_name: form.emergency_f_name || null,
        emergency_l_name: form.emergency_l_name || null,
        emergency_backup_phone: form.emergency_backup_phone || null,
        emergency_relationship: form.emergency_relationship || null,
        notifications_enabled: parseInt(form.notifications_enabled),
        children_ids: parentRecord?.children_ids || [],
      };

      if (isEdit) {
        await apiFetch(`/contacts/${parentRecord?.id || editParentId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }, token);
      } else {
        await apiFetch('/contacts', {
          method: 'POST',
          body: JSON.stringify(body),
        }, token);
      }
      navigate(returnPage, returnPage === 'drivers'
        ? { schoolId: effectiveSchoolId, selectDriverId: parentRecord?.id || editParentId }
        : effectiveSchoolId);
    } catch (err) {
      if (!isEdit && err.status === 409 && err.data?.existing_contact) {
        setExistingParent(err.data.existing_contact);
      } else {
        setError(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="sa-empty">Loading...</div>;
  if (isEdit && !parentRecord) return <div className="sa-error">{contactLabel} not found</div>;
  if (recordSchoolMismatch) return <div className="sa-error">{contactLabel} not found for this school</div>;

  return (
    <div>
      <div className="sa-breadcrumb">
        <button onClick={() => navigate('dashboard')}>Schools</button>
        {' / '}
        <button onClick={() => navigate('school-detail', effectiveSchoolId)}>School</button>
        {' / '}
        <button onClick={() => navigate(returnPage, effectiveSchoolId)}>{directoryLabel}</button>
        {' / '}
        {isEdit ? `Edit: ${parentRecord?.f_name || ''} ${parentRecord?.l_name || ''}`.trim() : 'Add Parent'}
      </div>

      <div className="sa-card">
        <h2>{isEdit ? `Edit ${contactLabel}` : 'Add Parent'}</h2>

        {error && <div className="sa-error">{error}</div>}

        {existingParent && (
          <div className="sa-card" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 16 }}>
            <p style={{ marginBottom: 6 }}>
              A parent account with this {String(existingParent.email).toLowerCase() === form.email.trim().toLowerCase() ? 'email' : 'phone number'} already exists:{' '}
              <strong>{existingParent.f_name} {existingParent.l_name}</strong> ({existingParent.email}, {existingParent.phone}),
              currently linked to {parseSchoolIds(existingParent.school_ids).length} school(s).
            </p>
            <p style={{ marginBottom: 12, color: '#555' }}>
              Parents use one account across schools. You can add the existing account to this school instead of creating a new one —
              they'll pick the school inside the app.
            </p>
            <div className="sa-btn-row">
              <button type="button" className="sa-btn sa-btn-primary" onClick={handleLinkExisting} disabled={linking}>
                {linking ? 'Adding...' : 'Add existing parent to this school'}
              </button>
              <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setExistingParent(null)} disabled={linking}>
                Keep editing
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <PhotoUploadField
            value={form.photo_relative_path}
            onChange={(path) => setForm((prev) => ({ ...prev, photo_relative_path: path }))}
            token={token}
          />

          <h3 style={{ fontSize: 15, color: '#555', marginBottom: 12 }}>Personal Information</h3>
          <div className="sa-form-grid">
            <div className="sa-field">
              <label>First Name *</label>
              <input value={form.f_name} onChange={set('f_name')} required />
            </div>
            <div className="sa-field">
              <label>Last Name *</label>
              <input value={form.l_name} onChange={set('l_name')} required />
            </div>
            <div className="sa-field">
              <label>Email *</label>
              <input type="email" value={form.email} onChange={set('email')} required />
            </div>
            <div className="sa-field">
              <label>Phone *</label>
              <input value={form.phone} onChange={set('phone')} required />
            </div>
          </div>

          <h3 style={{ fontSize: 15, color: '#555', margin: '20px 0 12px' }}>Address</h3>
          <div className="sa-form-grid">
            <div className="sa-field full-width">
              <label>Street Address *</label>
              <input value={form.street_address} onChange={set('street_address')} required />
            </div>
            <div className="sa-field">
              <label>City *</label>
              <input value={form.city} onChange={set('city')} required />
            </div>
            <div className="sa-field">
              <label>State *</label>
              <input value={form.state} onChange={set('state')} maxLength={2} placeholder="FL" required />
            </div>
            <div className="sa-field">
              <label>ZIP Code *</label>
              <input value={form.zip_code} onChange={set('zip_code')} required />
            </div>
          </div>

          <h3 style={{ fontSize: 15, color: '#555', margin: '20px 0 12px' }}>Vehicle & Driver Info</h3>
          <div className="sa-form-grid">
            <div className="sa-field">
              <label>Driver License #</label>
              <input value={form.driver_license_number} onChange={set('driver_license_number')} />
            </div>
            <div className="sa-field">
              <label>License Plate</label>
              <input value={form.license_plate} onChange={set('license_plate')} />
            </div>
            <div className="sa-field">
              <label>Vehicle Make/Model</label>
              <input value={form.vehicle_make_model} onChange={set('vehicle_make_model')} placeholder="e.g. Toyota Camry" />
            </div>
            <div className="sa-field">
              <label>Car Color</label>
              <input value={form.car_color} onChange={set('car_color')} placeholder="e.g. White" />
            </div>
          </div>

          <h3 style={{ fontSize: 15, color: '#555', margin: '20px 0 12px' }}>Emergency Contact</h3>
          <div className="sa-form-grid">
            <div className="sa-field">
              <label>Emergency First Name</label>
              <input value={form.emergency_f_name} onChange={set('emergency_f_name')} />
            </div>
            <div className="sa-field">
              <label>Emergency Last Name</label>
              <input value={form.emergency_l_name} onChange={set('emergency_l_name')} />
            </div>
            <div className="sa-field">
              <label>Emergency Phone</label>
              <input value={form.emergency_backup_phone} onChange={set('emergency_backup_phone')} />
            </div>
            <div className="sa-field">
              <label>Relationship</label>
              <select value={form.emergency_relationship} onChange={set('emergency_relationship')}>
                <option value="">Select...</option>
                {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <div className="sa-field" style={{ maxWidth: 200 }}>
              <label>Notifications</label>
              <select value={form.notifications_enabled} onChange={set('notifications_enabled')}>
                <option value="1">Enabled</option>
                <option value="0">Disabled</option>
              </select>
            </div>
          </div>

          <div className="sa-btn-row">
            <button type="submit" className="sa-btn sa-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? `Update ${contactLabel}` : 'Create Parent'}
            </button>
            <button type="button" className="sa-btn sa-btn-secondary" onClick={() => navigate(returnPage, returnPage === 'drivers'
              ? { schoolId: effectiveSchoolId, selectDriverId: parentRecord?.id || editParentId }
              : effectiveSchoolId)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
